import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ShoppingBagIcon } from "@heroicons/react/24/outline";
import { ordersAPI } from "../services/api";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import { format } from "date-fns";

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  paid: "bg-blue-100 text-blue-800",
  processing: "bg-purple-100 text-purple-800",
  shipped: "bg-indigo-100 text-indigo-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
  return_requested: "bg-orange-100 text-orange-800",
  returned: "bg-gray-100 text-gray-600",
};

export default function OrdersPage() {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ordersAPI.list().then((res) => {
      setOrders(res.data.items || []);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner className="py-20" size="lg" />;

  return (
    <>
      <Helmet><title>My Orders – Kushi Fashions</title></Helmet>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">My Orders</h1>

        {orders.length === 0 ? (
          <div className="text-center py-20">
            <ShoppingBagIcon className="h-16 w-16 text-gray-200 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No orders yet</p>
            <Link to="/products" className="btn-primary mt-4 inline-block py-2.5 px-6">Start Shopping</Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <Link key={order.id} to={`/orders/${order.id}`} className="card p-5 block hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-semibold text-gray-800">#{order.order_number}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {format(new Date(order.created_at), "dd MMM yyyy, h:mm a")}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {order.items?.length || 0} item{order.items?.length !== 1 ? "s" : ""}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`badge ${STATUS_COLORS[order.status] || "bg-gray-100 text-gray-600"}`}>
                      {order.status.replace("_", " ")}
                    </span>
                    <p className="font-bold text-primary-600 mt-2">
                      ₹{Number(order.total_amount).toLocaleString("en-IN")}
                    </p>
                  </div>
                </div>
                {order.tracking_number && (
                  <p className="text-xs text-gray-400 mt-2">
                    Tracking: {order.tracking_number} via {order.shipping_provider}
                  </p>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
